import AppContext from "@/AppContext";
import SortMobile from "@/components/drawer/sort-mobile";
import InfobyteViewer from "@/components/infobytes/InfobyteViewer";
import Layout from "@/components/elements/Layout";
import LoadingContent from "@/components/loader/LoadingContent";
import ContentWishlistComponent from "@/components/parts/ContentWishlistComponent";
import Schema from "@/components/schema/Schema";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGetContentList } from "@/hooks/content";
import { useIsMobile } from "@/hooks/useIsMobile";
import { getSEOByPathname } from "@/services/api/SEO";
import { getContentsByContentTypeName } from "@/services/api";
import { CONTENT_TYPES } from "@/utils/content";
import { fitIn } from "@/utils/imageHandler";
import {
  ArrowsDownUp,
  CaretDown,
  MagnifyingGlass,
  X,
} from "@phosphor-icons/react";
import Image from "next/legacy/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useContext, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

export default function Infobytes({ infobytes, seoData }) {
  const router = useRouter();
  const isMobile = useIsMobile();
  const appContext = useContext(AppContext);
  const onFetchError = function (error) {
    console.log("Error", error);
    toast.error("An error occured when fetching infobytes data!");
  };
  const {
    contents,
    isLoading,
    currentPage,
    fetchContents,
    search,
    setSearch,
    topic,
    setTopic,
    lastPage,
    total,
    sortInput,
    setSortInput,
    tags,
    setTags,
  } = useGetContentList({
    type: CONTENT_TYPES.INFOBYTES,
    onErrorCallback: onFetchError,
    // initialData: infobytes,
    is_random: true,
  });

  useEffect(() => {
    const { q } = router.query;
    if (q) {
      setSearch(q);
    }
  }, [router.query]);

  // Clicking a card opens the viewer over the grid and shows the infobyte's
  // own URL without leaving this page (shallow routing with `as`), so the
  // grid keeps its list and scroll. A refresh or shared link hits the real
  // /infobytes/[slug] page.
  const viewerSlug =
    typeof router.query.infobyte === "string" ? router.query.infobyte : null;
  const openedFromGridRef = useRef(false);
  const lastViewedSlugRef = useRef(null);
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => setIsMounted(true), []);

  const showInViewer = (slug, method) => {
    lastViewedSlugRef.current = slug;
    router[method](
      { pathname: router.pathname, query: { ...router.query, infobyte: slug } },
      `/infobytes/${slug}`,
      { shallow: true, scroll: false }
    );
  };

  const openViewer = (e, slug) => {
    // Let modified clicks open the real page in a new tab or window.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
      return;
    }
    e.preventDefault();
    openedFromGridRef.current = true;
    showInViewer(slug, "push");
  };

  // Back/Forward between the grid and the viewer would otherwise be a full
  // route change: getServerSideProps and _app's data fetch run again and the
  // viewer only closes once they finish. Keep those moves shallow.
  useEffect(() => {
    router.beforePopState(({ url, as, options }) => {
      if (as.split("?")[0] !== "/infobytes" && !url.includes("infobyte=")) {
        return true;
      }
      router.replace(url, as, { ...options, shallow: true, scroll: false });
      return false;
    });
    return () => router.beforePopState(() => true);
  }, [router]);

  // Moving through items replaces the entry, so Back closes the viewer
  // instead of stepping back through every item seen.
  const changeViewerSlug = (slug) => showInViewer(slug, "replace");

  const closeViewer = () => {
    if (openedFromGridRef.current) {
      openedFromGridRef.current = false;
      router.back();
      return;
    }
    const { infobyte, ...query } = router.query;
    router.replace({ pathname: router.pathname, query }, undefined, {
      shallow: true,
      scroll: false,
    });
  };

  // After closing, bring the last viewed card into view if the viewer
  // moved past what was on screen.
  useEffect(() => {
    if (viewerSlug || !lastViewedSlugRef.current) return;
    const card = document.getElementById(
      `infobyte-card-${lastViewedSlugRef.current}`
    );
    lastViewedSlugRef.current = null;
    if (!card) return;
    const { top, bottom } = card.getBoundingClientRect();
    if (top < 120 || bottom > window.innerHeight) {
      card.scrollIntoView({ block: "center" });
    }
  }, [viewerSlug]);

  const handleScroll = () => {
    const isBottom =
      window.innerHeight + window.scrollY >= document.body.offsetHeight - 1;
    if (isBottom && currentPage < lastPage && !isLoading) {
      fetchContents(currentPage + 1);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [currentPage, lastPage, isLoading]);

  const structuredData = [
    {
      "@context": "https://schema.org/",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: `${process.env.BASE_URL}`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Infobytes",
          item: `${process.env.BASE_URL}infobytes`,
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: infobytes.map((item, index) => {
        return {
          "@type": "ListItem",
          position: index + 1,
          url: `${process.env.BASE_URL}infobytes/${item.slug}`,
          name: item.title,
        };
      }),
    },
  ];

  const onChange = (e) => {
    setSortInput(e);
  };

  const [selectedTopics, setSelectedTopics] = useState([]);
  const [openTopicFilter, setOpenTopicFilter] = useState(false);
  const [searchTopicQuery, setSearchTopicQuery] = useState("");
  const [tempSelectedTopics, setTempSelectedTopics] = useState([]);

  const filteredTopics = appContext.state.topic_list.filter((topic) =>
    topic.name.toLowerCase().includes(searchTopicQuery.toLowerCase())
  );

  const handleTopicChange = (event, topic) => {
    const isChecked = event.target.checked;
    setTempSelectedTopics((prev) =>
      isChecked ? [...prev, topic] : prev.filter((t) => t.uuid !== topic.uuid)
    );
  };

  const applyTopicFilter = () => {
    setSelectedTopics(tempSelectedTopics);
    setTags(tempSelectedTopics.map((topic) => topic.name).join(";"));
    setOpenTopicFilter(false);
  };

  const removeTopicFilter = () => {
    setTempSelectedTopics([]);
    setSelectedTopics([]);
    setTags("");
  };

  useEffect(() => {
    setTempSelectedTopics(selectedTopics);
  }, [openTopicFilter]);

  const handleSearchInput = (evt) => {
    const searchValue = evt.target.value;
    if (searchValue === "" && sortInput === "") {
      setSortInput("newest");
    }
    setSearch(searchValue);

    // Remove 'q' parameter from URL when search is cleared, only if it exists
    if (searchValue === "" && router.query.q) {
      const { q, ...query } = router.query;
      router.push(
        {
          pathname: router.pathname,
          query: query,
        },
        undefined,
        { shallow: true }
      );
    }
  };

  return (
    <div>
      <Schema data={structuredData} />
      <Layout
        seoData={seoData}
        title='Infobytes'
        description={
          "Explore our engaging visuals & key insights on venture capital firms, VC funding, & the startup landscape in Dubai, UAE, MENA, & Africa."
        }
      >
        <div className='container py-8'>
          <h1 className='text-4xl font-semibold leading-tight text-white lg:font-bold lg:text-6xl font-display'>
            Infobytes
          </h1>
          <p className='mt-3 text-base leading-relaxed lg:mt-5 lg:text-lg text-white/50'>
            {seoData.page_description ||
              `Infobytes are our visual explainers: a single chart, table, or map answering one question about the MENAPT technology economy. They distil funding data, market sizing, adoption rates, and ecosystem rankings into a format you can read in under a minute and cite in a deck. Every Infobyte is built from primary data our research team collects and verifies. Browse the latest below, or filter by topic to find the numbers behind a specific sector or country.`}
          </p>
          <div className='flex flex-wrap items-center mt-5 gap-x-2 border-white/10 gap-y-3 lg:mt-8'>
            <div className='relative w-full'>
              <div className='absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none opacity-40'>
                <MagnifyingGlass className='text-lg' />
              </div>
              <Input
                type='text'
                placeholder='Search...'
                value={search}
                onInput={handleSearchInput}
                className='w-full transition-colors text-sm lg:text-base placeholder:text-white/40 !h-10 lg:!h-12 text-white rounded-lg bg-white/10 pl-10 focus:bg-white/15'
              />
            </div>
            <p className='flex-1 text-xs lg:text-base'>
              <span className='opacity-30'>Showing</span>{" "}
              {contents.length > 0 ? `1 - ${contents.length}` : 0}{" "}
              <span className='opacity-30'>of</span> {total}
            </p>
            {!isMobile ? (
              <Select value={sortInput} onValueChange={onChange}>
                <SelectTrigger
                  hideDropdownIcon
                  className='w-auto text-white border h-9 border-white/5 bg-white/5 hover:bg-white/10'
                >
                  <div className='flex items-center space-x-2'>
                    <ArrowsDownUp className='text-base lg:text-lg' />
                    <SelectValue placeholder={"Most Recent"} />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={"newest"}>Most Recent</SelectItem>
                  <SelectItem value={"most_viewed"}>Most Viewed</SelectItem>
                  {search && <SelectItem value={""}>Most Relevant</SelectItem>}
                </SelectContent>
              </Select>
            ) : (
              <SortMobile
                sortOption={sortInput}
                onChange={onChange}
                options={[
                  { value: "newest", label: "Most Recent" },
                  { value: "most_viewed", label: "Most Viewed" },
                  { value: "", label: "Most Relevant" },
                ]}
              />
            )}
            <Popover open={openTopicFilter} onOpenChange={setOpenTopicFilter}>
              <PopoverTrigger asChild>
                <Button
                  onClick={() => setOpenTopicFilter(true)}
                  className='text-white border h-9 border-white/5 bg-white/5 hover:bg-white/10'
                >
                  Topics
                  {selectedTopics.length > 0 && (
                    <span className='ml-1 inline-flex items-center justify-center w-4 h-4 font-semibold bg-white text-[10px] tracking-tighter leading-none rounded-full text-background'>
                      <span>{selectedTopics.length}</span>
                    </span>
                  )}
                  <CaretDown className='size-4 ml-2.5 -mr-1' />
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-64 bg-black h-80'>
                <div>
                  <Input
                    type='text'
                    placeholder='Search topics...'
                    value={searchTopicQuery}
                    onChange={(e) => setSearchTopicQuery(e.target.value)}
                    className='mb-2'
                  />
                </div>
                <ScrollArea className='w-56 h-56'>
                  {filteredTopics.map((topic, index) => (
                    <div key={index} className='flex items-center p-2 text-xs'>
                      <input
                        type='checkbox'
                        checked={
                          !!tempSelectedTopics.find(
                            (t) => t.uuid === topic.uuid
                          )
                        }
                        onChange={(event) => handleTopicChange(event, topic)}
                        id={`topic-${topic.uuid}`}
                        className='mr-2'
                      />
                      <label htmlFor={`topic-${topic.uuid}`}>
                        {topic.name}
                      </label>
                    </div>
                  ))}
                </ScrollArea>
                <div className='border-b border-r border-l rounded-b mt-2 pt-2 flex items-center justify-between p-2 bg-black -ml-4 w-[114.8%]'>
                  <Button
                    onClick={removeTopicFilter}
                    size='sm'
                    variant='link'
                    className='text-xs text-red-500 h-7'
                    disabled={selectedTopics.length <= 0}
                  >
                    Reset
                  </Button>
                  <Button
                    onClick={applyTopicFilter}
                    size='sm'
                    variant='outline'
                    className='text-xs h-7'
                  >
                    Apply Filter
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
            {selectedTopics && selectedTopics.length > 0 && (
              <div className='flex flex-wrap order-5 w-full gap-2'>
                {selectedTopics.map((topic) => (
                  <Badge
                    key={`selected-topic-${topic.uuid}`}
                    variant='secondary'
                    className='text-white bg-white/10 hover:bg-white/20'
                  >
                    {topic.name}
                    <button
                      className='ml-1 hover:text-red-500'
                      onClick={() => {
                        setSelectedTopics((prev) =>
                          prev.filter((t) => t.uuid !== topic.uuid)
                        );
                        setTags((prev) =>
                          prev
                            .split(";")
                            .filter((t) => t !== topic.name)
                            .join(";")
                        );
                      }}
                    >
                      <X size={14} />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {contents.length > 0 && (
            <h2 className='pt-8 text-xl font-semibold tracking-tight lg:pt-10 lg:text-2xl'>
              Latest MENAPT Data Infobytes
            </h2>
          )}
          {contents.length > 0 ? (
            <div className='grid grid-cols-2 gap-2 mt-5 lg:mt-8 lg:gap-4 lg:grid-cols-5'>
              {contents.map((item, index) => (
                <div
                  className='relative'
                  key={`infobyte-${item.uuid}`}
                  id={`infobyte-card-${item.slug}`}
                >
                  <div className='absolute z-20 top-1 right-1'>
                    <ContentWishlistComponent
                      item={item}
                      content_type_name_prop={item.content_type.name}
                      isDarkBackground={false}
                    />
                  </div>
                  <Link
                    legacyBehavior
                    passHref
                    href={`/infobytes/${item.slug}`}
                  >
                    <a
                      onClick={(e) => openViewer(e, item.slug)}
                      className='block relative aspect-[335/250] transition-transform origin-center duration-300 lg:hover:z-10 lg:hover:scale-[110%]'>
                      <Image
                        src={fitIn(item.thumbnail_url, 600)}
                        objectFit='cover'
                        layout='fill'
                        alt='Image'
                      />
                    </a>
                  </Link>
                </div>
              ))}
            </div>
          ) : !isLoading ? (
            <div className='flex flex-col items-center justify-center py-16'>
              <MagnifyingGlass
                size={48}
                className='mb-4 text-white opacity-50'
              />
              <p className='text-lg text-white text-opacity-80'>
                No infobytes found
              </p>
              <p className='mt-2 text-white text-opacity-60'>
                Try adjusting your search or filters
              </p>
            </div>
          ) : null}

          {isLoading && (
            <div className='flex justify-center mt-8'>
              <LoadingContent />
            </div>
          )}
        </div>

        {isMounted && viewerSlug && (
          <InfobyteViewer
            items={contents}
            total={total}
            slug={viewerSlug}
            hasMore={currentPage < lastPage}
            isLoading={isLoading}
            onLoadMore={() => fetchContents(currentPage + 1)}
            onSlugChange={changeViewerSlug}
            onClose={closeViewer}
          />
        )}
      </Layout>
    </div>
  );
}

export async function getServerSideProps() {
  let infobytes = [];
  try {
    const infobytesData = await getContentsByContentTypeName(
      CONTENT_TYPES.INFOBYTES
    );
    infobytes = infobytesData?.data?.data || [];
  } catch (error) {
    console.error("Error fetching infobytes for structured data:", error);
  }

  let seoData = {};
  try {
    const seoResponse = await getSEOByPathname("infobytes");
    seoData = seoResponse.data;
  } catch (error) {
    console.error("Error fetching SEO data:", error);
  }

  return {
    props: {
      infobytes,
      seoData,
    },
  };
}
